import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"
import _imports_0 from '@/assets/icons/arrow-left.svg'


const _hoisted_1 = { class: "flex flex-col gap-6 mt-4" }
const _hoisted_2 = { class: "items-center w-full" }
const _hoisted_3 = ["innerHTML"]
const _hoisted_4 = { class: "items-center md:max-w-[384px]" }
const _hoisted_5 = { class: "items-center md:max-w-[384px]" }
const _hoisted_6 = { class: "flex w-[640px] items-start content-start gap-4 flex-wrap mt-6" }
const _hoisted_7 = { class: "flex items-center space-x-2" }
const _hoisted_8 = ["disabled"]

import { onMounted, ref, watch, nextTick } from 'vue';
import { FormKit } from '@formkit/vue';
import { useAccountStore } from '@/store';
import RACQFormKit from '@/components/RACQFormKit/RACQFormKit.vue';
import PhoneNumberInput from '@/components/PhoneNumberInput/PhoneNumberInput.vue'; //
import FullScreenSpinner from '@/components/FullScreenSpinner/FullScreenSpinner.vue';
import { ISitecoreFormGenericInput } from '@/interfaces';
import { ToastAlertType } from '@/enums/toast-alert-enum';
import { useToastAlertStore } from '@/store/toast-alert/toast-alert.state';
import { cloneDeep } from 'lodash-es';
import { getFormInput } from '@/utils/form-input-helper';
import { Link } from '@sitecore-jss/sitecore-jss-vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'ContactInformationForm',
  props: {
    emailAddressInput: {},
    phoneNumberInput: {},
    additionalPhoneNumberInput: {},
    profileLink: {},
    content: {}
  },
  emits: ['emailIsTaken', 'update:modelValue', 'blur'],
  setup(__props: any, { emit: __emit }) {

const accountStore = useAccountStore();
const toastAlertStore = useToastAlertStore();

const emit = __emit;

const emailEl = ref<any>();
const primaryPhoneNumberEl = ref<any>();
const additionalPhoneNumberEl = ref<any>();

const emailAddress = ref<string>('');
const primaryPhoneNumber = ref<string>('');
const additionalPhoneNumber = ref<string>('');
const showSaving = ref<boolean>(false);
const hasChanges = ref<boolean>(false);
const emailAlreadyInUse = ref<boolean>(false);
const watcherReady = ref(false);

const props = __props;

const createBinding = (input: ISitecoreFormGenericInput, type: string) => {
  const bindings = {
    label: input?.label?.value,
    name: input?.name,
    type: type,
    placeholder: '',
    postContent: input?.postContent?.value,
    preContent: input?.preContent?.value,
    postContentClass: 'richtext',
    preContentClass: 'richtext',
    prefixIcon: '',
    suffixIcon: '',
    help: '',
    helpSm: '',
    helpMd: '',
    disablePaste: false,
    classesOptions: '',
    showClearIcon: false,
    minlength: null,
    maxlength: 100,
    autocomplete: 'off',
    innerClass: input?.innerClass?.value,
    outerClass: input?.outerClass?.value,
  };

  setupValidation(bindings, input);
  return bindings;
};

const setupValidation = (bindings: any, input: ISitecoreFormGenericInput) => {
  if (input?.isRequired?.value) {
    bindings.validation = [...(bindings.validation || []), ['required', 'trim']];

    if (input?.isRequiredErrorMessage?.value) {
      bindings.validationMessages = {
        ...(bindings.validationMessages || {}),
        required: input?.isRequiredErrorMessage?.value,
      };
    }
  }

  if (input?.validationRegex?.value) {
    bindings.validation = [...(bindings.validation || []), ['matches', new RegExp(input.validationRegex.value)]];

    bindings.validationRegex = input?.validationRegex?.value;

    if (input?.validationRegexErrorMessage?.value) {
      bindings.validationMessages = {
        ...(bindings.validationMessages || {}),
        matches: input?.validationRegexErrorMessage?.value,
      };
    }
  }
};

const discardChangesLink = ref(getFormInput(props.content, 'discardChangesLink'));
const emailBindings = createBinding(props.emailAddressInput, 'email');
const phoneNumberBindings = createBinding(props.phoneNumberInput, 'tel');
const additionalPhoneNumberBindings = createBinding(props.additionalPhoneNumberInput, 'tel');

onMounted(() => {
  setFieldsFromIdentityDetails();

  const mainElement = document.querySelector('main');
  if (mainElement) {
    mainElement.classList.add('grow-0');
  }
});

watch(
  [
    emailAddress,
    primaryPhoneNumber,
    additionalPhoneNumber,
    primaryPhoneNumberEl.value?.node?.store?.rule_matches?.blocking,
    additionalPhoneNumberEl.value?.node?.store?.rule_matches?.blocking,
  ],
  () => {
    if (watcherReady.value) {
      hasChanges.value = true;
    }
  }
);

const checkEmail = async (e: any) => {
  //If have blocking regex don't bother validating
  if (emailEl.value?.inputRef?.node?.store?.rule_matches?.blocking) {
    emailAlreadyInUse.value = true;
    return;
  }

  try {
    emailAlreadyInUse.value = false;
    if (e?.target?.value) {
      const newEmail = e.target.value?.toLowerCase();
      if (accountStore.identityDetails?.email?.toLowerCase() != newEmail) {
        const emailIsTaken = await accountStore.isUserNameTaken(newEmail);
        emailAlreadyInUse.value = emailIsTaken;
        emit('emailIsTaken', emailIsTaken);
        return;
      }

      accountStore.fetchIdentityDetails(true);
      emailAlreadyInUse.value = false;
      emit('emailIsTaken', false);
    }
  } catch (error) {
    emit('emailIsTaken', false);
  }
};

const setFieldsFromIdentityDetails = () => {
  emailAddress.value = cloneDeep(accountStore?.identityDetails?.email ?? '');
  primaryPhoneNumber.value = cloneDeep(accountStore?.identityDetails?.phoneNumber ?? '');
  additionalPhoneNumber.value = cloneDeep(accountStore?.identityDetails?.secondaryPhoneNumber ?? '');

  nextTick(() => {
    hasChanges.value = false;
    watcherReady.value = true;
  });
};

const showUnableToSaveMessage = () => {
  toastAlertStore.addToastAlert({
    title: 'Unable to save changes',
    description: '',
    type: ToastAlertType.ERROR,
    duration: 6000,
  });
};

const onSave = async () => {
  try {
    if (accountStore.identityDetails?.email?.toLowerCase() != emailAddress.value.toLowerCase()) {
      const emailIsTaken = await accountStore.isUserNameTaken(emailAddress.value);
      if (emailIsTaken) {
        emailAlreadyInUse.value = true;
        showUnableToSaveMessage();
        return;
      }
    }

    if (
      emailEl.value?.inputRef?.node?.store?.rule_matches?.blocking ||
      primaryPhoneNumberEl.value?.node?.store?.rule_matches?.blocking ||
      additionalPhoneNumberEl.value?.node?.store?.rule_matches?.blocking
    ) {
      return;
    }

    showSaving.value = true;

    await accountStore.updateContactInformation(
      accountStore.identityDetails?.d365Id ?? '',
      emailAddress.value,
      primaryPhoneNumber.value,
      additionalPhoneNumber.value ?? null
    );

    hasChanges.value = false;
    showSaving.value = false;

    toastAlertStore.addToastAlert({
      title: 'Contact information updated',
      description: '',
      type: ToastAlertType.SUCCESS,
      duration: 6000,
    });
  } catch (error) {
    showSaving.value = false;
    showUnableToSaveMessage();
  }
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(FormKit), {
    id: "contactInformationForm",
    name: "contactInformationForm",
    type: "form",
    onSubmit: onSave
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(RACQFormKit, {
            ref_key: "emailEl",
            ref: emailEl,
            modelValue: emailAddress.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((emailAddress).value = $event)),
            bindings: _unref(emailBindings),
            onBlur: _cache[1] || (_cache[1] = (e) => checkEmail(e))
          }, null, 8, ["modelValue", "bindings"]),
          _createElementVNode("p", {
            class: "text-xs",
            innerHTML: _ctx.emailAddressInput?.postContent?.value
          }, null, 8, _hoisted_3)
        ]),
        _createElementVNode("div", _hoisted_4, [
          _createVNode(PhoneNumberInput, {
            ref_key: "primaryPhoneNumberEl",
            ref: primaryPhoneNumberEl,
            modelValue: primaryPhoneNumber.value,
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((primaryPhoneNumber).value = $event)),
            bindings: _unref(phoneNumberBindings)
          }, null, 8, ["modelValue", "bindings"])
        ]),
        _createElementVNode("div", _hoisted_5, [
          _createVNode(PhoneNumberInput, {
            ref_key: "additionalPhoneNumberEl",
            ref: additionalPhoneNumberEl,
            modelValue: additionalPhoneNumber.value,
            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((additionalPhoneNumber).value = $event)),
            bindings: _unref(additionalPhoneNumberBindings)
          }, null, 8, ["modelValue", "bindings"])
        ]),
        _createElementVNode("div", _hoisted_6, [
          _createVNode(_unref(Link), {
            field: discardChangesLink.value?.link,
            class: "btn btn-tertiary btn-md"
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_7, [
                _cache[4] || (_cache[4] = _createElementVNode("img", {
                  src: _imports_0,
                  alt: "left arrow",
                  class: "w-5 h-5 inline-block"
                }, null, -1)),
                _createElementVNode("span", null, _toDisplayString(discardChangesLink.value?.link?.value?.text), 1)
              ])
            ]),
            _: 1
          }, 8, ["field"]),
          _createElementVNode("button", {
            type: "submit",
            disabled: !hasChanges.value || emailAlreadyInUse.value,
            class: "btn btn-md btn-primary"
          }, " Save changes ", 8, _hoisted_8)
        ])
      ]),
      (showSaving.value)
        ? (_openBlock(), _createBlock(FullScreenSpinner, {
            key: 0,
            applyOpacity: true
          }))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }))
}
}

})